import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, XCircle, Building2, User, Lock } from 'lucide-react';
import api from './Api.js';
import { useAppTheme } from '../../contexts/ThemeContext';
import backgroundImage from './photo2.jpg';

// ── Sub-components defined OUTSIDE Register to prevent remount on every keystroke ──

const PasswordReq = ({ met, text, successColor, errorColor }) => (
  <div className="flex items-center gap-2 text-xs">
    {met
      ? <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: successColor }} />
      : <XCircle     className="w-4 h-4 flex-shrink-0" style={{ color: errorColor }} />}
    <span style={{ color: met ? successColor : errorColor }}>{text}</span>
  </div>
);

const SectionHead = ({ icon: Icon, label, iconColor, textColor }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon className="w-5 h-5" style={{ color: iconColor }} />
    <h2 className="text-xl font-semibold" style={{ color: textColor }}>{label}</h2>
  </div>
);

const Field = ({
  name, label, type = 'text', required = false, placeholder = '',
  value, onChange, disabled, error,
  inputCls, inputStyle, labelStyle, errorColor, primaryColor, borderColor,
}) => (
  <div>
    <label className="block text-sm font-medium mb-1" style={labelStyle}>
      {label} {required && <span style={{ color: errorColor }}>*</span>}
    </label>
    <input
      type={type} name={name} value={value} onChange={onChange}
      disabled={disabled} placeholder={placeholder}
      className={inputCls}
      style={inputStyle}
      onFocus={e => e.target.style.borderColor = primaryColor}
      onBlur={e  => e.target.style.borderColor = error ? errorColor : borderColor}
    />
    {error && <p className="text-xs mt-1" style={{ color: errorColor }}>{error}</p>}
  </div>
);

const Register = () => {
  const navigate = useNavigate();
  const { palette, Colors, mode } = useAppTheme();

  const P     = Colors.primary.main;
  const Pdark = Colors.primary.dark;

  const [formData, setFormData] = useState({
    companyName: '', email: '', address: '', website: '', phone: '',
    password: '', confirmPassword: '', firstName: '', lastName: ''
  });
  const [errors,          setErrors]          = useState({});
  const [showPassword,    setShowPassword]    = useState(false);
  const [showConfirmPw,   setShowConfirmPw]   = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [loading,         setLoading]         = useState(false);

  const passwordCriteria = {
    minLength:     formData.password.length >= 8,
    hasUppercase:  /[A-Z]/.test(formData.password),
    hasLowercase:  /[a-z]/.test(formData.password),
    hasNumber:     /\d/.test(formData.password),
    hasSpecialChar:/[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
  };
  const isPasswordValid = Object.values(passwordCriteria).every(Boolean);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name])    setErrors({ ...errors, [name]: '' });
    if (submissionError) setSubmissionError('');
  };

  const validateForm = () => {
    const e = {};
    if (!formData.firstName) e.firstName = 'First name is required';
    if (!formData.lastName)  e.lastName  = 'Last name is required';
    if (!formData.email)         e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Email is invalid';
    if (!formData.address)       e.address = 'Address is required';
    if (!formData.phone)         e.phone = 'Phone number is required';
    else if (!/^[\d\s()-]+$/.test(formData.phone)) e.phone = 'Phone number is invalid';
    if (!formData.password)      e.password = 'Password is required';
    else if (!isPasswordValid)   e.password = 'Password does not meet requirements';
    if (!formData.confirmPassword)                             e.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword)   e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setSubmissionError(''); setLoading(true);
    try {
      await api.auth.register({
        name: formData.companyName, address: formData.address, email: formData.email,
        phone_number: formData.phone, website: formData.website || undefined,
        password: formData.password, first_name: formData.firstName, last_name: formData.lastName
      });
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      let msg = 'Registration failed. Please try again.';
      if (err.response?.status === 400) {
        const d = err.response.data;
        if (d.message) msg = d.message;
        else if (d.errors) {
          const be = {};
          if (d.errors.email)        be.email       = d.errors.email[0];
          if (d.errors.password)     be.password    = d.errors.password[0];
          if (d.errors.phone_number) be.phone       = d.errors.phone_number[0];
          if (d.errors.name)         be.companyName = d.errors.name[0];
          if (Object.keys(be).length) { setErrors(be); msg = 'Please fix the errors below and try again.'; }
        } else if (d.detail) msg = d.detail;
      } else if (err.message) msg = err.message;
      setSubmissionError(msg);
    } finally { setLoading(false); }
  };

  // ── Style helpers ─────────────────────────────────────────────
  const cardBg     = mode === 'dark' ? palette.background.secondary : 'rgba(255,255,255,0.95)';
  const cardBorder = mode === 'dark' ? palette.border               : 'rgba(255,255,255,0.2)';

  const labelStyle   = { color: palette.text.secondary };
  const iconStyle    = { color: P };
  const dividerStyle = { borderColor: palette.border };

  const inputStyle = (hasError = false) => ({
    backgroundColor: palette.background.secondary,
    color:           palette.text.primary,
    borderColor:     hasError ? Colors.status.error : palette.border,
    caretColor:      P,
  });

  const inputCls = (hasError) =>
    `w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${loading ? 'opacity-60' : ''}`;

  // Sub-components are now defined outside Register (above) to avoid remounting on every render.

  return (
    <div
      className="fixed inset-0 bg-cover bg-center bg-no-repeat flex items-center justify-center p-4"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="w-full max-w-4xl h-[90vh] flex items-center justify-center">
        <div
          className="backdrop-blur-lg rounded-2xl shadow-2xl w-full max-h-full overflow-hidden flex flex-col"
          style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
        >
          {/* Fixed header */}
          <div
            className="flex-shrink-0 text-center pt-8 pb-4 px-8 border-b"
            style={dividerStyle}
          >
            <h1 className="text-4xl font-bold mb-2" style={{
              background: `linear-gradient(to right, ${Pdark}, ${P})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>
              Rental Management
            </h1>
            <p className="text-lg font-medium" style={{ color: palette.text.secondary }}>
              Create Your Account
            </p>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">

            {/* Submission error */}
            {submissionError && (
              <div className="mb-6 p-4 rounded-lg" style={{
                backgroundColor: `${Colors.status.error}12`,
                border: `1px solid ${Colors.status.error}40`
              }}>
                <p className="text-sm" style={{ color: Colors.status.error }}>{submissionError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">

              {/* ── Company Details ── */}
              <div>
                <SectionHead icon={Building2} label="Company Details" iconColor={P} textColor={P} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field name="companyName" label="Company Name" required
                    value={formData.companyName} onChange={handleChange} disabled={loading} error={errors.companyName}
                    inputCls={inputCls()} inputStyle={inputStyle(!!errors.companyName)}
                    labelStyle={labelStyle} errorColor={Colors.status.error} primaryColor={P} borderColor={palette.border} />
                  <Field name="email" label="Email Address" type="email" required
                    value={formData.email} onChange={handleChange} disabled={loading} error={errors.email}
                    inputCls={inputCls()} inputStyle={inputStyle(!!errors.email)}
                    labelStyle={labelStyle} errorColor={Colors.status.error} primaryColor={P} borderColor={palette.border} />

                  <div className="md:col-span-2">
                    <Field name="address" label="Address" required placeholder="123 Business Ave, Suite 500"
                      value={formData.address} onChange={handleChange} disabled={loading} error={errors.address}
                      inputCls={inputCls()} inputStyle={inputStyle(!!errors.address)}
                      labelStyle={labelStyle} errorColor={Colors.status.error} primaryColor={P} borderColor={palette.border} />
                  </div>

                  <Field name="website" label="Website (Optional)" placeholder="www.yourcompany.com"
                    value={formData.website} onChange={handleChange} disabled={loading} error={errors.website}
                    inputCls={inputCls()} inputStyle={inputStyle(!!errors.website)}
                    labelStyle={labelStyle} errorColor={Colors.status.error} primaryColor={P} borderColor={palette.border} />
                  <Field name="phone" label="Phone Number" type="tel" required placeholder="(555) 123-4567"
                    value={formData.phone} onChange={handleChange} disabled={loading} error={errors.phone}
                    inputCls={inputCls()} inputStyle={inputStyle(!!errors.phone)}
                    labelStyle={labelStyle} errorColor={Colors.status.error} primaryColor={P} borderColor={palette.border} />
                </div>
              </div>

              <div className="border-t" style={dividerStyle} />

              {/* ── Admin User Details ── */}
              <div>
                <SectionHead icon={User} label="Admin User Details" iconColor={P} textColor={P} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field name="firstName" label="First Name" required
                    value={formData.firstName} onChange={handleChange} disabled={loading} error={errors.firstName}
                    inputCls={inputCls()} inputStyle={inputStyle(!!errors.firstName)}
                    labelStyle={labelStyle} errorColor={Colors.status.error} primaryColor={P} borderColor={palette.border} />
                  <Field name="lastName"  label="Last Name" required
                    value={formData.lastName} onChange={handleChange} disabled={loading} error={errors.lastName}
                    inputCls={inputCls()} inputStyle={inputStyle(!!errors.lastName)}
                    labelStyle={labelStyle} errorColor={Colors.status.error} primaryColor={P} borderColor={palette.border} />
                </div>
              </div>

              <div className="border-t" style={dividerStyle} />

              {/* ── Account Security ── */}
              <div>
                <SectionHead icon={Lock} label="Account Security" iconColor={P} textColor={P} />
                <div className="space-y-4">

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={labelStyle}>
                      Password <span style={{ color: Colors.status.error }}>*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'} name="password" value={formData.password}
                        onChange={handleChange} disabled={loading}
                        className={`${inputCls(errors.password)} pr-10`}
                        style={inputStyle(!!errors.password)}
                        onFocus={e => e.target.style.borderColor = P}
                        onBlur={e  => e.target.style.borderColor = errors.password ? Colors.status.error : palette.border}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={loading}
                        className="absolute right-3 top-1/2 -translate-y-1/2 transition"
                        style={{ color: palette.text.tertiary }}>
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-xs mt-1" style={{ color: Colors.status.error }}>{errors.password}</p>}

                    {/* Password requirements */}
                    {formData.password && (
                      <div className="mt-3 p-3 rounded-lg space-y-2" style={{
                        backgroundColor: palette.background.tertiary,
                        border: `1px solid ${palette.border}`,
                      }}>
                        <p className="text-xs font-medium mb-2" style={{ color: palette.text.secondary }}>
                          Password requirements:
                        </p>
                        <PasswordReq met={passwordCriteria.minLength}     text="At least 8 characters"              successColor={Colors.status.success} errorColor={Colors.status.error} />
                        <PasswordReq met={passwordCriteria.hasUppercase}   text="One uppercase letter"               successColor={Colors.status.success} errorColor={Colors.status.error} />
                        <PasswordReq met={passwordCriteria.hasLowercase}   text="One lowercase letter"               successColor={Colors.status.success} errorColor={Colors.status.error} />
                        <PasswordReq met={passwordCriteria.hasNumber}      text="One number"                         successColor={Colors.status.success} errorColor={Colors.status.error} />
                        <PasswordReq met={passwordCriteria.hasSpecialChar}  text="One special character (!@#$%^&*)"  successColor={Colors.status.success} errorColor={Colors.status.error} />
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={labelStyle}>
                      Confirm Password <span style={{ color: Colors.status.error }}>*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPw ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword}
                        onChange={handleChange} disabled={loading}
                        className={`${inputCls(errors.confirmPassword)} pr-10`}
                        style={inputStyle(!!errors.confirmPassword)}
                        onFocus={e => e.target.style.borderColor = P}
                        onBlur={e  => e.target.style.borderColor = errors.confirmPassword ? Colors.status.error : palette.border}
                      />
                      <button type="button" onClick={() => setShowConfirmPw(!showConfirmPw)} disabled={loading}
                        className="absolute right-3 top-1/2 -translate-y-1/2 transition"
                        style={{ color: palette.text.tertiary }}>
                        {showConfirmPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-xs mt-1" style={{ color: Colors.status.error }}>{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit */}
              <button type="submit" disabled={loading}
                className="w-full py-3 px-4 text-white font-semibold rounded-lg shadow-lg transform transition hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg"
                style={{ background: loading ? P : `linear-gradient(to right, ${Pdark}, ${P})` }}>
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating Account...
                  </span>
                ) : 'Create Account'}
              </button>

              <div className="text-center pb-4">
                <RouterLink to="/login" className="text-sm font-medium hover:underline" style={{ color: P }}>
                  Already have an account? Sign in
                </RouterLink>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;